import React, { PropsWithChildren } from 'react'
import { createContext } from 'lib/createContext'
import { useAddRuleJurisdictionMutation } from 'queries/wage/useAddRuleJurisdictionMutation'
import { useJurisdictionsQuery } from 'queries/wage/useJurisdictionsQuery'
import { useRuleDetailContextValue } from '../../../context/RuleDetailProvider'
import { parseErrorMessage } from 'api'
import { formatJurisdictionOptionLabel } from 'pages/Wage/utils'

type JurisdictionOption = { label: string; value: number }

type AttachJurisdictionFlowContextState = {
  state: {
    jurisdictionOptions: JurisdictionOption[]
    mutationState: {
      isPending: boolean
      errorMessage: string | undefined
    }
  }
  actions: {
    submit: ReturnType<typeof useAddRuleJurisdictionMutation>['mutate']
  }
}

const [Context, useAttachJurisdictionFlowContextValue] =
  createContext<AttachJurisdictionFlowContextState>({
    name: 'AttachJurisdictionFlowContext',
  })

export const AttachJurisdictionFlowProvider = ({
  children,
  ruleId,
}: PropsWithChildren<{ ruleId: number }>) => {
  const { mutate, isPending, error } = useAddRuleJurisdictionMutation(ruleId)

  const {
    state: { jurisdictionIds },
  } = useRuleDetailContextValue()

  const { data: jurisdictions } = useJurisdictionsQuery()

  const jurisdictionOptions: JurisdictionOption[] = (jurisdictions ?? [])
    .filter((j) => !jurisdictionIds.includes(j.id))
    .map((j) => ({ label: formatJurisdictionOptionLabel(j), value: j.id }))

  const value: AttachJurisdictionFlowContextState = {
    state: {
      jurisdictionOptions,
      mutationState: {
        isPending,
        errorMessage: parseErrorMessage(error),
      },
    },
    actions: { submit: mutate },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useAttachJurisdictionFlowContextValue }
